import type { ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';

import { captureEvent } from '@/services/analytics';
import TextField from '@/shared/ui/primitives/TextField';

type TextFieldProps = ComponentProps<typeof TextField>;

const debounce = <Args extends unknown[]>(
  func: (...args: Args) => void,
  delay: number
): ((...args: Args) => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export type PostHogTextFieldProps = TextFieldProps & {
  id: string;
};

const PostHogTextField = ({ id, value, onClick, onKeyDown, ...rest }: PostHogTextFieldProps) => {
  const debouncedCapture = useMemo(
    () =>
      debounce((inputValue: string) => {
        if (inputValue.trim() !== '') {
          captureEvent('gxgo_text_field_submitted', {
            id,
            searchValue: inputValue
          });
        }
      }, 2000),
    [id]
  );

  const capturePostHogSearch = useCallback(
    (inputValue: string) => {
      debouncedCapture(inputValue);
    },
    [debouncedCapture]
  );

  const getCurrentValue = (event: React.SyntheticEvent) => {
    const { target } = event;

    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      return target.value;
    }

    // Fallback to controlled value prop
    return typeof value === 'string' ? value : '';
  };

  const handleClick: TextFieldProps['onClick'] = event => {
    capturePostHogSearch(getCurrentValue(event));
    onClick?.(event);
  };

  const handleKeyDown: TextFieldProps['onKeyDown'] = event => {
    capturePostHogSearch(getCurrentValue(event));
    onKeyDown?.(event);
  };

  return (
    <TextField
      {...rest}
      id={id}
      value={value}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    />
  );
};

export default PostHogTextField;
